import React, { useState, useEffect, useMemo } from 'react';
import {
  X,
  Plus,
  Trash2,
  UploadCloud,
  AlertCircle,
  Building2,
  Wheat,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { arrivalService } from '@/services/arrivalService';
import { feedService, BulkFeedInvoiceItem } from '@/services/feedService';
import { cycleService } from '@/services/cycleService';
import { Combobox } from '@/components/common/Combobox';
import { formatCurrency } from '@/lib/utils';
import {
  SupplierRecord,
  FeedProductRecord,
  ProductionCycleRecord,
} from '@/types/database';

interface BulkFeedInvoiceModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  initialCycleId?: string;
}

interface EditableLineItem extends BulkFeedInvoiceItem {
  tempId: string;
}

export const BulkFeedInvoiceModal: React.FC<BulkFeedInvoiceModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  initialCycleId,
}) => {
  const [suppliers, setSuppliers] = useState<SupplierRecord[]>([]);
  const [products, setProducts] = useState<FeedProductRecord[]>([]);
  const [cycles, setCycles] = useState<ProductionCycleRecord[]>([]);
  const [loadingLookups, setLoadingLookups] = useState<boolean>(true);

  // Invoice Header
  const [supplierId, setSupplierId] = useState<string>('');
  const [invoiceNo, setInvoiceNo] = useState<string>('');
  const [deliveryReceiptNo, setDeliveryReceiptNo] = useState<string>('');
  const [receiptDate, setReceiptDate] = useState<string>(
    new Date().toISOString().split('T')[0]
  );
  const [headerNotes, setHeaderNotes] = useState<string>('');
  const [attachedFile, setAttachedFile] = useState<File | null>(null);

  // Line Items
  const [lineItems, setLineItems] = useState<EditableLineItem[]>([]);

  // Submission & Validation States
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // Load lookup records on mount / open
  useEffect(() => {
    if (!isOpen) return;

    const fetchLookups = async () => {
      setLoadingLookups(true);
      setErrorMsg(null);
      try {
        const [sups, prods, cycs] = await Promise.all([
          arrivalService.getSuppliers(),
          feedService.getFeedProducts(),
          cycleService.getCycles(),
        ]);

        setSuppliers(sups);
        setProducts(prods);
        setCycles(cycs);

        if (sups.length > 0 && !supplierId) {
          const feedSup =
            sups.find(
              (s: SupplierRecord) =>
                s.supplier_type === 'feed' || s.supplier_name.toLowerCase().includes('feed')
            ) || sups[0];
          setSupplierId(feedSup.id);
        }

        // Initialize with 1 line item
        if (lineItems.length === 0) {
          const defaultCycleId = initialCycleId || (cycs.length > 0 ? cycs[0].id : '');
          const defaultProd = prods.length > 0 ? prods[0] : null;
          setLineItems([
            {
              tempId: `line-${Date.now()}-1`,
              cycle_id: defaultCycleId,
              product_id: defaultProd ? defaultProd.id : '',
              quantity_bags: 50,
              unit_price: defaultProd?.default_unit_price
                ? Number(defaultProd.default_unit_price)
                : 0,
              feed_markup_amount: 0,
              trucking_cost: 0,
              notes: '',
            },
          ]);
        }
      } catch (err: any) {
        console.error('Error fetching bulk feed modal lookups:', err);
        setErrorMsg('Failed to load reference suppliers, products, and cycles.');
      } finally {
        setLoadingLookups(false);
      }
    };

    fetchLookups();
  }, [isOpen, initialCycleId]);

  // Combobox option maps
  const supplierOptions = useMemo(() => {
    return suppliers.map((s) => ({
      value: s.id,
      label: s.supplier_name,
    }));
  }, [suppliers]);

  const cycleOptions = useMemo(() => {
    return cycles.map((c) => ({
      value: c.id,
      label: `${c.cycle_code} — ${c.growers?.full_name || 'Company-Owned'}`,
      badge: c.status,
    }));
  }, [cycles]);

  const productOptions = useMemo(() => {
    return products.map((p) => ({
      value: p.id,
      label: p.product_name,
      sublabel: p.default_unit_price ? `₱${Number(p.default_unit_price).toFixed(2)}/bag` : undefined,
    }));
  }, [products]);

  // Handle adding a new line
  const handleAddLine = () => {
    const defaultCycleId = initialCycleId || (cycles.length > 0 ? cycles[0].id : '');
    const defaultProd = products.length > 0 ? products[0] : null;
    const newLine: EditableLineItem = {
      tempId: `line-${Date.now()}-${lineItems.length + 1}`,
      cycle_id: defaultCycleId,
      product_id: defaultProd ? defaultProd.id : '',
      quantity_bags: 50,
      unit_price: defaultProd?.default_unit_price
        ? Number(defaultProd.default_unit_price)
        : 0,
      feed_markup_amount: 0,
      trucking_cost: 0,
      notes: '',
    };
    setLineItems([...lineItems, newLine]);
  };

  // Handle removing a line
  const handleRemoveLine = (index: number) => {
    if (lineItems.length <= 1) return;
    const updated = [...lineItems];
    updated.splice(index, 1);
    setLineItems(updated);
  };

  // Handle line item field change
  const handleLineChange = (index: number, field: keyof EditableLineItem, value: any) => {
    const updated = [...lineItems];
    const item = { ...updated[index], [field]: value };

    // If product changed, update default unit price
    if (field === 'product_id') {
      const prod = products.find((p) => p.id === value);
      if (prod && prod.default_unit_price) {
        item.unit_price = Number(prod.default_unit_price);
      }
    }

    updated[index] = item;
    setLineItems(updated);
  };

  // Totals calculations
  const totalBags = useMemo(() => {
    return lineItems.reduce((sum, item) => sum + (Number(item.quantity_bags) || 0), 0);
  }, [lineItems]);

  const totalInvoiceAmount = useMemo(() => {
    return lineItems.reduce((sum, item) => {
      const baseCost = (Number(item.quantity_bags) || 0) * (Number(item.unit_price) || 0);
      const markup = Number(item.feed_markup_amount) || 0;
      const trucking = Number(item.trucking_cost) || 0;
      return sum + baseCost + markup + trucking;
    }, 0);
  }, [lineItems]);

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg(null);

    if (!supplierId) {
      setErrorMsg('Please select a supplier.');
      return;
    }
    if (!receiptDate) {
      setErrorMsg('Please select an invoice / delivery date.');
      return;
    }
    if (lineItems.length === 0) {
      setErrorMsg('At least one cycle allocation line is required.');
      return;
    }

    for (let i = 0; i < lineItems.length; i++) {
      const item = lineItems[i];
      if (!item.cycle_id) {
        setErrorMsg(`Line #${i + 1}: Destination cycle is required.`);
        return;
      }
      if (!item.product_id) {
        setErrorMsg(`Line #${i + 1}: Feed product is required.`);
        return;
      }
      if (!item.quantity_bags || item.quantity_bags <= 0) {
        setErrorMsg(`Line #${i + 1}: Quantity in bags must be greater than 0.`);
        return;
      }
      if (item.unit_price < 0) {
        setErrorMsg(`Line #${i + 1}: Unit price cannot be negative.`);
        return;
      }
    }

    setSubmitting(true);
    try {
      await feedService.createBulkFeedInvoice(
        {
          supplier_id: supplierId,
          invoice_no: invoiceNo.trim() || null,
          delivery_receipt_no: deliveryReceiptNo.trim() || null,
          receipt_date: receiptDate,
          notes: headerNotes.trim() || null,
          items: lineItems.map((item) => ({
            cycle_id: item.cycle_id,
            product_id: item.product_id,
            quantity_bags: Number(item.quantity_bags),
            unit_price: Number(item.unit_price),
            feed_markup_amount: Number(item.feed_markup_amount) || 0,
            trucking_cost: Number(item.trucking_cost) || 0,
            total_weight_kg: Number(item.quantity_bags) * 50,
            notes: item.notes?.trim() || null,
          })),
        },
        attachedFile
      );

      onSuccess();
      onClose();
    } catch (err: any) {
      console.error('Error saving bulk feed invoice:', err);
      setErrorMsg(err.message || 'Failed to save bulk feed invoice.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-slate-900/60 backdrop-blur-xs overflow-y-auto">
      <div className="bg-white rounded-xl shadow-2xl border border-slate-200 w-full max-w-5xl my-6 flex flex-col max-h-[92vh] overflow-hidden animate-in fade-in zoom-in-95 duration-150">
        {/* Modal Header */}
        <div className="flex items-center justify-between px-5 sm:px-6 py-3.5 sm:py-4 border-b border-slate-200 bg-slate-50 flex-shrink-0">
          <div className="flex items-center gap-2.5">
            <div className="p-2 rounded-lg bg-emerald-100 text-emerald-800 flex-shrink-0">
              <Wheat className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-base sm:text-lg font-bold text-slate-900">Bulk Supplier Feed Invoice</h2>
              <p className="text-[11px] sm:text-xs text-slate-500">
                Direct Supplier → Production Cycle multi-line feed allocation under one invoice.
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={submitting}
            aria-label="Close modal"
            className="p-1.5 text-slate-400 hover:text-slate-700 rounded-lg hover:bg-slate-100 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Modal Body / Scrollable Form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-5">
          {errorMsg && (
            <div className="flex items-start gap-2.5 p-3.5 rounded-lg bg-red-50 text-red-900 border border-red-200 text-xs">
              <AlertCircle className="w-4 h-4 text-red-600 shrink-0 mt-0.5" />
              <span>{errorMsg}</span>
            </div>
          )}

          {/* Section 1: Supplier Invoice Header */}
          <div className="bg-slate-50/80 p-3.5 sm:p-4 rounded-xl border border-slate-200 space-y-3.5">
            <h3 className="text-xs font-bold uppercase tracking-wider text-slate-700 flex items-center gap-1.5">
              <Building2 className="w-4 h-4 text-emerald-700" />
              1. Supplier Invoice Header
            </h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">
                  Feed Supplier <span className="text-red-500">*</span>
                </label>
                <Combobox
                  options={supplierOptions}
                  value={supplierId}
                  onChange={setSupplierId}
                  placeholder="Select Supplier..."
                  searchPlaceholder="Search supplier..."
                  disabled={loadingLookups || submitting}
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">
                  Invoice Date <span className="text-red-500">*</span>
                </label>
                <Input
                  type="date"
                  value={receiptDate}
                  onChange={(e) => setReceiptDate(e.target.value)}
                  disabled={submitting}
                  className="h-9 text-xs bg-white"
                  required
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">
                  Invoice Number
                </label>
                <Input
                  type="text"
                  placeholder="e.g. INV-2026-0811"
                  value={invoiceNo}
                  onChange={(e) => setInvoiceNo(e.target.value)}
                  disabled={submitting}
                  className="h-9 text-xs font-mono bg-white"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">
                  Delivery Receipt # (DR)
                </label>
                <Input
                  type="text"
                  placeholder="e.g. DR-5842"
                  value={deliveryReceiptNo}
                  onChange={(e) => setDeliveryReceiptNo(e.target.value)}
                  disabled={submitting}
                  className="h-9 text-xs font-mono bg-white"
                />
              </div>
            </div>

            {/* Document Attachment & Notes */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 pt-2 border-t border-slate-200/80">
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">
                  Invoice Document / DR Scan (Optional)
                </label>
                <div className="flex items-center gap-2">
                  <label className="flex-1 flex items-center justify-center gap-2 px-3 py-2 border border-dashed border-slate-300 rounded-lg cursor-pointer bg-white hover:border-emerald-500 transition-colors text-xs text-slate-600 min-h-[38px]">
                    <UploadCloud className="w-4 h-4 text-slate-400" />
                    <span className="truncate">
                      {attachedFile ? attachedFile.name : 'Choose file or drag & drop (PDF, JPG, PNG)'}
                    </span>
                    <input
                      type="file"
                      accept=".pdf,image/*"
                      onChange={(e) => {
                        if (e.target.files && e.target.files[0]) {
                          setAttachedFile(e.target.files[0]);
                        }
                      }}
                      className="hidden"
                    />
                  </label>
                  {attachedFile && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setAttachedFile(null)}
                      className="text-xs text-red-600 hover:text-red-700 h-8 px-2"
                    >
                      Clear
                    </Button>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">
                  Invoice Notes / Hauler Info
                </label>
                <Input
                  type="text"
                  placeholder="e.g. Direct mill truck delivery to Batangas cluster"
                  value={headerNotes}
                  onChange={(e) => setHeaderNotes(e.target.value)}
                  disabled={submitting}
                  className="h-9 text-xs bg-white"
                />
              </div>
            </div>
          </div>

          {/* Section 2: Multi-Cycle Line Allocations */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-xs font-bold uppercase tracking-wider text-slate-700">
                  2. Destination Production Cycle Allocations
                </h3>
                <p className="text-[11px] text-slate-500">
                  Allocate quantities directly to grower production cycles.
                </p>
              </div>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={handleAddLine}
                disabled={submitting}
                className="text-xs h-8 gap-1 border-emerald-300 text-emerald-800 hover:bg-emerald-50 font-semibold"
              >
                <Plus className="w-3.5 h-3.5 text-emerald-700" /> Add Cycle Allocation
              </Button>
            </div>

            {/* Desktop Table View (hidden on mobile) */}
            <div className="hidden md:block overflow-x-auto border border-slate-200 rounded-lg">
              <table className="w-full text-left text-xs border-collapse">
                <thead className="bg-slate-100/90 text-slate-700 font-semibold border-b border-slate-200">
                  <tr>
                    <th className="py-2.5 px-3 w-10 text-center">#</th>
                    <th className="py-2.5 px-3 min-w-[200px]">Destination Cycle *</th>
                    <th className="py-2.5 px-3 min-w-[180px]">Feed Product *</th>
                    <th className="py-2.5 px-3 w-28 text-right">Bags *</th>
                    <th className="py-2.5 px-3 w-32 text-right">Unit Price (₱) *</th>
                    <th className="py-2.5 px-3 w-28 text-right">Markup (₱)</th>
                    <th className="py-2.5 px-3 w-28 text-right">Trucking (₱)</th>
                    <th className="py-2.5 px-3 w-32 text-right">Subtotal (₱)</th>
                    <th className="py-2.5 px-2 w-10 text-center"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 bg-white font-mono">
                  {lineItems.map((line, idx) => {
                    const lineFeedCost =
                      (Number(line.quantity_bags) || 0) * (Number(line.unit_price) || 0);
                    const lineMarkup = Number(line.feed_markup_amount) || 0;
                    const lineTrucking = Number(line.trucking_cost) || 0;
                    const lineSubtotal = lineFeedCost + lineMarkup + lineTrucking;

                    return (
                      <tr key={line.tempId} className="hover:bg-slate-50/80 transition-colors">
                        <td className="py-2 px-3 text-center text-slate-400 font-sans">{idx + 1}</td>
                        <td className="py-2 px-3">
                          <Combobox
                            options={cycleOptions}
                            value={line.cycle_id}
                            onChange={(val) => handleLineChange(idx, 'cycle_id', val)}
                            placeholder="Select Cycle..."
                            searchPlaceholder="Search cycle..."
                            disabled={submitting}
                          />
                        </td>
                        <td className="py-2 px-3">
                          <Combobox
                            options={productOptions}
                            value={line.product_id}
                            onChange={(val) => handleLineChange(idx, 'product_id', val)}
                            placeholder="Select Product..."
                            searchPlaceholder="Search product..."
                            disabled={submitting}
                          />
                        </td>
                        <td className="py-2 px-3 text-right">
                          <Input
                            type="number"
                            min="1"
                            step="1"
                            inputMode="numeric"
                            value={line.quantity_bags}
                            onChange={(e) => handleLineChange(idx, 'quantity_bags', Number(e.target.value))}
                            disabled={submitting}
                            className="h-8 text-xs text-right font-mono px-2"
                            required
                          />
                        </td>
                        <td className="py-2 px-3 text-right">
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            inputMode="decimal"
                            value={line.unit_price}
                            onChange={(e) => handleLineChange(idx, 'unit_price', Number(e.target.value))}
                            disabled={submitting}
                            className="h-8 text-xs text-right font-mono px-2"
                            required
                          />
                        </td>
                        <td className="py-2 px-3 text-right">
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            inputMode="decimal"
                            value={line.feed_markup_amount || 0}
                            onChange={(e) => handleLineChange(idx, 'feed_markup_amount', Number(e.target.value))}
                            disabled={submitting}
                            className="h-8 text-xs text-right font-mono px-2"
                          />
                        </td>
                        <td className="py-2 px-3 text-right">
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            inputMode="decimal"
                            value={line.trucking_cost || 0}
                            onChange={(e) => handleLineChange(idx, 'trucking_cost', Number(e.target.value))}
                            disabled={submitting}
                            className="h-8 text-xs text-right font-mono px-2"
                          />
                        </td>
                        <td className="py-2 px-3 text-right font-bold text-slate-900">
                          {formatCurrency(lineSubtotal)}
                        </td>
                        <td className="py-2 px-2 text-center font-sans">
                          <button
                            type="button"
                            onClick={() => handleRemoveLine(idx)}
                            disabled={lineItems.length <= 1 || submitting}
                            aria-label={`Remove Line #${idx + 1}`}
                            className="p-1 text-slate-400 hover:text-red-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                            title="Remove Line"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobile Touch Cards View (md:hidden) */}
            <div className="md:hidden space-y-3">
              {lineItems.map((line, idx) => {
                const lineFeedCost =
                  (Number(line.quantity_bags) || 0) * (Number(line.unit_price) || 0);
                const lineMarkup = Number(line.feed_markup_amount) || 0;
                const lineTrucking = Number(line.trucking_cost) || 0;
                const lineSubtotal = lineFeedCost + lineMarkup + lineTrucking;

                return (
                  <div
                    key={line.tempId}
                    className="p-3.5 bg-slate-50 border border-slate-200 rounded-xl space-y-3"
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-bold text-slate-700">Allocation Line #{idx + 1}</span>
                      {lineItems.length > 1 && (
                        <button
                          type="button"
                          onClick={() => handleRemoveLine(idx)}
                          className="text-xs text-red-600 font-semibold flex items-center gap-1"
                        >
                          <Trash2 className="w-3.5 h-3.5" /> Remove
                        </button>
                      )}
                    </div>

                    <div>
                      <label className="block text-[11px] font-bold text-slate-600 mb-1">Destination Cycle *</label>
                      <Combobox
                        options={cycleOptions}
                        value={line.cycle_id}
                        onChange={(val) => handleLineChange(idx, 'cycle_id', val)}
                        placeholder="Select Cycle..."
                        searchPlaceholder="Search cycle..."
                        disabled={submitting}
                      />
                    </div>

                    <div>
                      <label className="block text-[11px] font-bold text-slate-600 mb-1">Feed Product *</label>
                      <Combobox
                        options={productOptions}
                        value={line.product_id}
                        onChange={(val) => handleLineChange(idx, 'product_id', val)}
                        placeholder="Select Product..."
                        searchPlaceholder="Search product..."
                        disabled={submitting}
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="block text-[11px] font-bold text-slate-600 mb-1">Bags *</label>
                        <Input
                          type="number"
                          min="1"
                          inputMode="numeric"
                          value={line.quantity_bags}
                          onChange={(e) => handleLineChange(idx, 'quantity_bags', Number(e.target.value))}
                          className="h-9 text-xs font-mono text-right bg-white"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-[11px] font-bold text-slate-600 mb-1">Unit Price (₱) *</label>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          inputMode="decimal"
                          value={line.unit_price}
                          onChange={(e) => handleLineChange(idx, 'unit_price', Number(e.target.value))}
                          className="h-9 text-xs font-mono text-right bg-white"
                          required
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="block text-[11px] font-bold text-slate-600 mb-1">Markup (₱)</label>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          inputMode="decimal"
                          value={line.feed_markup_amount || 0}
                          onChange={(e) => handleLineChange(idx, 'feed_markup_amount', Number(e.target.value))}
                          className="h-9 text-xs font-mono text-right bg-white"
                        />
                      </div>
                      <div>
                        <label className="block text-[11px] font-bold text-slate-600 mb-1">Trucking (₱)</label>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          inputMode="decimal"
                          value={line.trucking_cost || 0}
                          onChange={(e) => handleLineChange(idx, 'trucking_cost', Number(e.target.value))}
                          className="h-9 text-xs font-mono text-right bg-white"
                        />
                      </div>
                    </div>

                    <div className="flex items-center justify-between pt-2 border-t border-slate-200 text-xs">
                      <span className="text-slate-500 font-medium">Line Subtotal:</span>
                      <span className="font-bold font-mono text-slate-900">{formatCurrency(lineSubtotal)}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Section 3: Summary Totals */}
          <div className="bg-slate-900 text-white rounded-xl p-4 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4 font-mono text-xs">
            <div className="flex items-center gap-6">
              <div>
                <span className="text-slate-400 block text-[10px] uppercase">Allocations</span>
                <span className="text-base font-bold text-white">{lineItems.length} lines</span>
              </div>
              <div>
                <span className="text-slate-400 block text-[10px] uppercase">Total Volume</span>
                <span className="text-base font-bold text-emerald-400">{totalBags.toLocaleString()} bags</span>
              </div>
            </div>

            <div className="text-left sm:text-right border-t sm:border-t-0 pt-2 sm:pt-0 border-slate-800">
              <span className="text-slate-400 block text-[10px] uppercase">Total Invoice Amount</span>
              <span className="text-xl font-bold text-emerald-400">
                {formatCurrency(totalInvoiceAmount)}
              </span>
            </div>
          </div>
        </form>

        {/* Modal Footer */}
        <div className="flex items-center justify-end gap-3 px-5 sm:px-6 py-3.5 sm:py-4 border-t border-slate-200 bg-slate-50 flex-shrink-0">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={submitting}
            className="text-xs h-9 px-4"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={submitting || loadingLookups}
            className="text-xs h-9 px-5 bg-emerald-700 hover:bg-emerald-600 text-white font-semibold gap-1.5 shadow-xs"
          >
            {submitting ? 'Saving Invoice...' : `Save Invoice (${lineItems.length} Lines)`}
          </Button>
        </div>
      </div>
    </div>
  );
};
